import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Upload, FileText, Download, TrendingUp, Trash2, Edit2, Mail } from "lucide-react";
import { PATIENT } from "@/constants/testIds";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export const PatientDetailPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const api = useApi(token);
  const [patient, setPatient] = useState(null);
  const [labResults, setLabResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resultToDelete, setResultToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedResultForEmail, setSelectedResultForEmail] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  
  // Prescription state
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedPrescriptionFile, setSelectedPrescriptionFile] = useState(null);
  const [uploadingPrescription, setUploadingPrescription] = useState(false);
  const [deletePrescriptionDialogOpen, setDeletePrescriptionDialogOpen] = useState(false);
  const [prescriptionToDelete, setPrescriptionToDelete] = useState(null);
  const [sendingReminderEmail, setSendingReminderEmail] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [prescriptionToReschedule, setPrescriptionToReschedule] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    date: "",
    time: "",
    notes: ""
  });
  
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    date_of_birth: "",
    gender: "",
    blood_group: "",
    phone: "",
    email: "",
    medical_history: ""
  });

  useEffect(() => {
    loadPatientData();
    loadPrescriptions();
  }, [patientId]);

  const loadPatientData = async () => {
    try {
      const patientData = await api.get(`/patients/${patientId}`);
      setPatient(patientData);
      setEditFormData({
        full_name: patientData.full_name || "",
        date_of_birth: patientData.date_of_birth || "",
        gender: patientData.gender || "",
        blood_group: patientData.blood_group || "",
        phone: patientData.phone || "",
        email: patientData.email || "",
        medical_history: patientData.medical_history || ""
      });
      const results = await api.get(`/files/${patientId}/lab-results`);
      setLabResults(results);
    } catch (error) {
      console.error("Failed to load patient data:", error);
    }
  };

  const loadPrescriptions = async () => {
    try {
      const data = await api.get(`/prescriptions/${patientId}/list`);
      console.log('Loaded prescriptions:', data);
      setPrescriptions(data);
    } catch (error) {
      console.error("Failed to load prescriptions:", error);
      setPrescriptions([]);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(
        `${API_URL}/files/upload-and-extract?patient_id=${patientId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setLabResults([response.data, ...labResults]);
      setSelectedFile(null);
      document.getElementById("fileInput").value = "";
      alert("File uploaded and analyzed successfully!");
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Failed to upload and process file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadPDF = async (resultId, patientName) => {
    try {
      const response = await axios.get(
        `${API_URL}/files/lab-results/${resultId}/export-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = `lab_report_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF download failed:", error);
      alert("Failed to download PDF report. Please try again.");
    }
  };

  const confirmDelete = (result) => {
    setResultToDelete(result);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!resultToDelete) return;
    
    try {
      await axios.delete(
        `${API_URL}/files/${patientId}/files/${resultToDelete.file_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setLabResults(labResults.filter(r => r.id !== resultToDelete.id));
      setDeleteDialogOpen(false);
      setResultToDelete(null);
      alert("Lab result deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete lab result. " + (error.response?.data?.detail || "Please try again."));
    }
  };

  const handleEditPatient = async (e) => {
    e.preventDefault();
    try {
      const updated = await api.put(`/patients/${patientId}`, editFormData);
      setPatient(updated);
      setEditDialogOpen(false);
      alert("Patient information updated successfully!");
    } catch (error) {
      console.error("Failed to update patient:", error);
      alert("Failed to update patient information.");
    }
  };

  const openEditDialog = () => {
    setEditFormData({
      full_name: patient.full_name || "",
      date_of_birth: patient.date_of_birth || "",
      gender: patient.gender || "",
      phone: patient.phone || "",
      email: patient.email || "",
      medical_history: patient.medical_history || ""
    });
    setEditDialogOpen(true);
  };

  const handleEmailReport = async () => {
    if (!selectedResultForEmail) return;
    
    const patientEmail = patient.email;
    if (!patientEmail) {
      toast.error("Patient email is not set. Please edit patient information to add an email address first.");
      return;
    }

    setEmailSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/files/lab-results/${selectedResultForEmail.id}/email-report`,
        { result_id: selectedResultForEmail.id, patient_email: patientEmail },
        {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        }
      );
      
      toast.success(`✓ Lab report sent successfully to ${patientEmail}!`, {
        description: response.data.message || "The patient will receive the AI analyzed report and original file.",
        duration: 5000
      });
      setEmailDialogOpen(false);
      setSelectedResultForEmail(null);
    } catch (error) {
      console.error("Email failed:", error);
      toast.error("Failed to send email", {
        description: error.response?.data?.detail || "Please try again.",
        duration: 5000
      });
    } finally {
      setEmailSending(false);
    }
  };

  const openEmailDialog = (result) => {
    setSelectedResultForEmail(result);
    setEmailDialogOpen(true);
  };

  const handlePrescriptionUpload = async (e, demoMode = false) => {
    e.preventDefault();
    if (!selectedPrescriptionFile) return;

    setUploadingPrescription(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedPrescriptionFile);

      const response = await axios.post(
        `${API_URL}/prescriptions/${patientId}/upload?demo_mode=${demoMode}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(`✓ Prescription uploaded successfully!`, {
        description: demoMode 
          ? "Follow-up set for 5 minutes from now (demo). Calendar invite sent!"
          : `Follow-up on ${new Date(response.data.follow_up_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. Calendar invite sent!`,
        duration: 5000
      });

      setSelectedPrescriptionFile(null);
      document.getElementById('prescriptionFileInput').value = '';
      await loadPrescriptions();
    } catch (error) {
      console.error("Prescription upload failed:", error);
      toast.error("Failed to upload prescription", {
        description: error.response?.data?.detail || "Please try again.",
        duration: 5000
      });
    } finally {
      setUploadingPrescription(false);
    }
  };

  const handleDeletePrescription = (prescription) => {
    setPrescriptionToDelete(prescription);
    setDeletePrescriptionDialogOpen(true);
  };

  const handleDeletePrescriptionConfirmed = async () => {
    if (!prescriptionToDelete) return;
    
    try {
      await api.delete(`/prescriptions/${prescriptionToDelete.id}`);
      toast.success("Prescription deleted successfully");
      setDeletePrescriptionDialogOpen(false);
      setPrescriptionToDelete(null);
      await loadPrescriptions();
    } catch (error) {
      console.error("Failed to delete prescription:", error);
      toast.error(error.response?.data?.detail || "Failed to delete prescription");
    }
  };

  const handleSendReminderEmail = async (prescription) => {
    if (!patient.email) {
      toast.error("Patient email not set. Please add an email address first.");
      return;
    }

    setSendingReminderEmail(true);
    try {
      await api.post(`/prescriptions/${prescription.id}/resend-email`);
      toast.success(`✓ Reminder email sent to ${patient.email}!`, {
        description: "Follow-up appointment reminder has been sent with calendar invite.",
        duration: 5000
      });
    } catch (error) {
      console.error("Failed to send reminder:", error);
      toast.error(error.response?.data?.detail || "Failed to send reminder email");
    } finally {
      setSendingReminderEmail(false);
    }
  };

  const handleToggleNoFollowup = async (prescription) => {
    const newValue = !prescription.no_followup;
    
    try {
      console.log('Toggling no_followup for prescription:', prescription.id, 'to:', newValue);
      const response = await api.patch(`/prescriptions/${prescription.id}/toggle-followup`, {
        no_followup: newValue
      });
      console.log('Toggle response:', response);
      
      toast.success(newValue ? "Marked as no follow-up needed" : "Follow-up tracking enabled");
      await loadPrescriptions();
    } catch (error) {
      console.error("Failed to toggle follow-up:", error);
      console.error("Error response:", error.response);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to update";
      toast.error(errorMessage);
    }
  };

  const handleOpenReschedule = (prescription) => {
    setPrescriptionToReschedule(prescription);
    
    // Pre-fill with current follow-up date if exists
    if (prescription.follow_up_date) {
      const followupDate = new Date(prescription.follow_up_date);
      const dateStr = followupDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = followupDate.toTimeString().slice(0, 5); // HH:MM
      setRescheduleData({
        date: dateStr,
        time: timeStr,
        notes: prescription.follow_up_notes || ""
      });
    } else {
      setRescheduleData({ date: "", time: "", notes: "" });
    }
    
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleData.date || !rescheduleData.time) {
      toast.error("Please select both date and time");
      return;
    }

    try {
      // Send datetime in ISO format without timezone conversion
      // This preserves the exact time selected by the user
      const dateTimeStr = `${rescheduleData.date}T${rescheduleData.time}:00`;
      
      await api.patch(`/prescriptions/${prescriptionToReschedule.id}/reschedule`, {
        follow_up_date: dateTimeStr,
        follow_up_notes: rescheduleData.notes
      });
      
      toast.success("✓ Follow-up rescheduled successfully!", {
        description: "Updated calendar invite has been sent to the patient.",
        duration: 5000
      });
      
      setRescheduleDialogOpen(false);
      setPrescriptionToReschedule(null);
      setRescheduleData({ date: "", time: "", notes: "" });
      await loadPrescriptions();
    } catch (error) {
      console.error("Failed to reschedule:", error);
      toast.error(error.response?.data?.detail || "Failed to reschedule");
    }
  };

  const formatPrescriptionDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
        <p style={{ color: "#555555", fontFamily: "Poppins, sans-serif" }}>Loading patient...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#fcfbf9", fontFamily: "Poppins, sans-serif" }}>
      <header className="border-b" style={{ backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)", borderColor: "#e5e5e5" }}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate("/dashboard")} 
              variant="ghost" 
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: "#652D90" }}>{patient.full_name}</h1>
              <div className="flex items-center gap-4 text-sm" style={{ color: "#555555" }}>
                {patient.email && <span>{patient.email}</span>}
                {patient.blood_group && (
                  <>
                    {patient.email && <span>•</span>}
                    <span className="font-medium text-red-600">Blood: {patient.blood_group}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={openEditDialog} 
              variant="outline"
              className="rounded-full"
              data-testid="edit-patient-button"
            >
              <Edit2 size={16} className="mr-2" />
              Edit Patient
            </Button>
            {labResults.length > 1 && (
              <Button 
                onClick={() => navigate(`/trends/${patientId}`)} 
                variant="outline"
                className="rounded-full"
              >
                <TrendingUp size={16} className="mr-2" />
                View Trends
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card className="rounded-2xl border-[#e5e5e5] mb-8">
          <CardHeader>
            <CardTitle style={{ color: "#652D90" }}>Upload Lab Report</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div>
                <Input
                  id="fileInput"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.tif,.mp4,.mov,.avi"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="rounded-xl"
                  data-testid={PATIENT.fileInput}
                />
                <p className="text-xs mt-2" style={{ color: "#555555" }}>Supported formats: PDF, Images (PNG, JPG), Videos (MP4, MOV, AVI)</p>
              </div>
              <Button 
                type="submit" 
                disabled={!selectedFile || uploading}
                className="rounded-full"
                style={{ backgroundColor: "#652D90" }}
                data-testid={PATIENT.uploadButton}
              >
                <Upload size={16} className="mr-2" />
                {uploading ? "Processing..." : "Upload & Analyze"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Prescription Upload Section */}
        <Card className="rounded-2xl border-[#e5e5e5] mb-8">
          <CardHeader>
            <CardTitle style={{ color: "#652D90" }}>Upload Prescription</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => handlePrescriptionUpload(e, false)} className="space-y-4">
              <div>
                <Input
                  id="prescriptionFileInput"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setSelectedPrescriptionFile(e.target.files?.[0] || null)}
                  className="rounded-xl"
                  data-testid="prescription-file-input"
                />
                <p className="text-xs mt-2" style={{ color: "#555555" }}>Supported formats: PDF, Images (PNG, JPG). AI will extract medicines and follow-up dates.</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={!selectedPrescriptionFile || uploadingPrescription}
                  className="rounded-full"
                  style={{ backgroundColor: "#652D90" }}
                  data-testid="upload-prescription-button"
                >
                  <Upload size={16} className="mr-2" />
                  {uploadingPrescription ? "Processing..." : "Upload & Extract"}
                </Button>
                <Button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    const submitEvent = { preventDefault: () => {} };
                    handlePrescriptionUpload(submitEvent, true);
                  }}
                  disabled={!selectedPrescriptionFile || uploadingPrescription}
                  variant="outline"
                  className="rounded-full"
                  data-testid="upload-prescription-demo-button"
                >
                  📅 Demo (5 min reminder)
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>


        <div>
          <h2 className="text-2xl font-bold mb-6" style={{ color: "#333333" }}>Lab Results</h2>
          {labResults.length === 0 ? (
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardContent className="py-12 text-center">
                <FileText size={48} className="mx-auto mb-4" style={{ color: "#652D90", opacity: 0.5 }} />
                <p style={{ color: "#555555" }}>No lab results yet. Upload a file to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {labResults.map((result) => (
                <Card key={result.id} className="rounded-2xl border-[#e5e5e5]" data-testid={PATIENT.labResultCard}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex flex-col gap-1">
                        <span style={{ color: "#333333" }}>{result.source_filename}</span>
                        {result.uploaded_by && (
                          <span className="text-xs font-normal" style={{ color: "#555555" }}>
                            Uploaded by: {result.uploaded_by}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-normal" style={{ color: "#555555" }}>
                          {new Date(result.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                        <Button
                          onClick={() => openEmailDialog(result)}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          data-testid="email-report-button"
                        >
                          <Mail size={16} className="mr-1" />
                          Email
                        </Button>
                        <Button
                          onClick={() => handleDownloadPDF(result.id, patient.full_name)}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          data-testid="download-pdf-button"
                        >
                          <Download size={16} className="mr-1" />
                          PDF
                        </Button>
                        <Button
                          onClick={() => confirmDelete(result)}
                          variant="outline"
                          size="sm"
                          className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid="delete-result-button"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="results" className="w-full">
                      <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="results">Results</TabsTrigger>
                        <TabsTrigger value="summary">Summary</TabsTrigger>
                        <TabsTrigger value="abnormal">Abnormal</TabsTrigger>
                        <TabsTrigger value="insights">Insights</TabsTrigger>
                      </TabsList>

                      <TabsContent value="results" className="mt-4">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b" style={{ borderColor: "#e5e5e5" }}>
                                <th className="text-left py-2" style={{ color: "#333333" }}>Test</th>
                                <th className="text-right py-2" style={{ color: "#333333" }}>Value</th>
                                <th className="text-left py-2" style={{ color: "#333333" }}>Unit</th>
                                <th className="text-left py-2" style={{ color: "#333333" }}>Reference Range</th>
                                <th className="text-left py-2" style={{ color: "#333333" }}>Trend</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.lab_values.map((val, idx) => {
                                // Always prefer the full reference range text if available
                                let refRangeDisplay = "—";
                                if (val.ref_range_text && val.ref_range_text.trim()) {
                                  refRangeDisplay = val.ref_range_text;
                                } else if (val.ref_low && val.ref_high) {
                                  refRangeDisplay = `${val.ref_low}-${val.ref_high}`;
                                } else if (val.ref_high) {
                                  refRangeDisplay = `< ${val.ref_high}`;
                                } else if (val.ref_low) {
                                  refRangeDisplay = `> ${val.ref_low}`;
                                }
                                
                                return (
                                  <tr key={idx} className="border-b" style={{ borderColor: "#e5e5e5" }}>
                                    <td className="py-2" style={{ color: "#333333" }}>{val.name}</td>
                                    <td 
                                      className="py-2 text-right font-semibold"
                                      style={{ 
                                        color: val.flag === "high" ? "#e90d01" : val.flag === "low" ? "#1fb25a" : "#333333" 
                                      }}
                                      data-testid={val.flag !== "normal" ? PATIENT.abnormalValue : undefined}
                                    >
                                      {val.value || val.value_str || "N/A"}
                                    </td>
                                    <td className="py-2" style={{ color: "#555555" }}>{val.unit}</td>
                                    <td className="py-2 text-sm" style={{ color: "#555555", maxWidth: "300px" }}>
                                      {refRangeDisplay}
                                    </td>
                                    <td className="py-2 text-sm">
                                      {val.comparison ? (
                                        <div>
                                          <div 
                                            className="font-semibold mb-1"
                                            style={{ 
                                              color: val.comparison.color === "green" ? "#1fb25a" : 
                                                     val.comparison.color === "red" ? "#e90d01" : "#ff8c00"
                                            }}
                                          >
                                            {val.comparison.trend} {val.comparison.message}
                                          </div>
                                          <div className="text-xs" style={{ color: "#666666" }}>
                                            Previous: {val.comparison.previous_value} {val.unit}
                                            <br />
                                            Change: {val.comparison.change > 0 ? "+" : ""}{val.comparison.change.toFixed(2)} 
                                            ({val.comparison.change_percent}%)
                                          </div>
                                        </div>
                                      ) : (
                                        <span style={{ color: "#999999" }}>—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </TabsContent>

                      <TabsContent value="summary" className="mt-4">
                        <Tabs defaultValue="english" className="w-full">
                          <TabsList>
                            <TabsTrigger value="english">English</TabsTrigger>
                            <TabsTrigger value="nepali">नेपाली (Nepali)</TabsTrigger>
                          </TabsList>
                          <TabsContent value="english" className="mt-4">
                            <div className="prose max-w-none">
                              <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                                {result.overall_summary || "No summary available."}
                              </p>
                            </div>
                          </TabsContent>
                          <TabsContent value="nepali" className="mt-4">
                            <div className="prose max-w-none">
                              <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                                {result.overall_summary_nepali || "नेपाली सारांश उपलब्ध छैन।"}
                              </p>
                            </div>
                          </TabsContent>
                        </Tabs>
                      </TabsContent>

                      <TabsContent value="abnormal" className="mt-4">
                        <Tabs defaultValue="english" className="w-full">
                          <TabsList>
                            <TabsTrigger value="english">English</TabsTrigger>
                            <TabsTrigger value="nepali">नेपाली (Nepali)</TabsTrigger>
                          </TabsList>
                          <TabsContent value="english" className="mt-4">
                            <div className="prose max-w-none">
                              <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                                {result.abnormal_highlights || "No abnormal values detected."}
                              </p>
                            </div>
                          </TabsContent>
                          <TabsContent value="nepali" className="mt-4">
                            <div className="prose max-w-none">
                              <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>
                                {result.abnormal_highlights_nepali || "कुनै असामान्य मानहरू पत्ता लगाइएको छैन।"}
                              </p>
                            </div>
                          </TabsContent>
                        </Tabs>
                      </TabsContent>

                      <TabsContent value="insights" className="mt-4">
                        <Tabs defaultValue="english" className="w-full">
                          <TabsList>
                            <TabsTrigger value="english">English</TabsTrigger>
                            <TabsTrigger value="nepali">नेपाली (Nepali)</TabsTrigger>
                          </TabsList>
                          <TabsContent value="english" className="mt-4">
                            <div className="space-y-4">
                              {result.trends_analysis && (
                                <div>
                                  <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>Trends Analysis</h4>
                                  <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>{result.trends_analysis}</p>
                                </div>
                              )}
                              {result.ai_insights && (
                                <div>
                                  <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>Clinical Insights</h4>
                                  <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>{result.ai_insights}</p>
                                </div>
                              )}
                              {!result.trends_analysis && !result.ai_insights && (
                                <p style={{ color: "#555555" }}>No additional insights available.</p>
                              )}
                            </div>
                          </TabsContent>
                          <TabsContent value="nepali" className="mt-4">
                            <div className="space-y-4">
                              {result.trends_analysis_nepali && (
                                <div>
                                  <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>प्रवृत्ति विश्लेषण</h4>
                                  <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>{result.trends_analysis_nepali}</p>
                                </div>
                              )}
                              {result.ai_insights_nepali && (
                                <div>
                                  <h4 className="font-semibold mb-2" style={{ color: "#652D90" }}>चिकित्सा सल्लाह</h4>
                                  <p style={{ color: "#333333", whiteSpace: "pre-wrap" }}>{result.ai_insights_nepali}</p>
                                </div>
                              )}
                              {!result.trends_analysis_nepali && !result.ai_insights_nepali && (
                                <p style={{ color: "#555555" }}>थप जानकारी उपलब्ध छैन।</p>
                              )}
                            </div>
                          </TabsContent>
                        </Tabs>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Prescriptions Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: "#333333" }}>Prescriptions & Follow-ups</h2>
          {prescriptions.length === 0 ? (
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardContent className="py-12 text-center">
                <FileText size={48} className="mx-auto mb-4" style={{ color: "#cccccc" }} />
                <p style={{ color: "#555555" }}>No prescriptions yet. Upload a prescription to track medicines and follow-ups.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {prescriptions.map((prescription) => (
                <Card key={prescription.id} className="rounded-2xl border-[#e5e5e5]">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex flex-col gap-1">
                        <span style={{ color: "#333333" }}>{prescription.source_filename}</span>
                        <span className="text-xs font-normal" style={{ color: "#555555" }}>
                          Uploaded by: {prescription.uploaded_by}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-normal" style={{ color: "#555555" }}>
                          {new Date(prescription.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                        {prescription.follow_up_date && !prescription.no_followup && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendReminderEmail(prescription)}
                            disabled={sendingReminderEmail}
                            className="rounded-full"
                            title="Send follow-up reminder email"
                          >
                            <Mail size={16} className="mr-1" />
                            {sendingReminderEmail ? "Sending..." : "Send Reminder"}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePrescription(prescription)}
                          className="text-red-600 hover:bg-red-50 rounded-full"
                          title="Delete prescription"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Doctor & Follow-up Info */}
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-semibold mb-1" style={{ color: "#555555" }}>Doctor:</p>
                          <p className="text-base" style={{ color: "#652D90" }}>{prescription.doctor_name || "N/A"}</p>
                          {prescription.doctor_nmc_number && (
                            <p className="text-xs mt-1" style={{ color: "#555555" }}>NMC: {prescription.doctor_nmc_number}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-semibold mb-1" style={{ color: "#555555" }}>Prescription Date:</p>
                          <p className="text-base" style={{ color: "#333333" }}>{formatPrescriptionDate(prescription.prescription_date)}</p>
                        </div>
                        {prescription.follow_up_date && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs font-semibold" style={{ color: "#555555" }}>Follow-up Date:</p>
                              {!prescription.no_followup && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenReschedule(prescription)}
                                  className="h-7 text-xs rounded-full"
                                  style={{ color: "#652D90" }}
                                >
                                  Reschedule
                                </Button>
                              )}
                            </div>
                            <p className="text-base font-semibold" style={{ color: "#1fb25a" }}>
                              📅 {new Date(prescription.follow_up_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} at {new Date(prescription.follow_up_date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                            </p>
                            {prescription.follow_up_notes && (
                              <p className="text-sm mt-1" style={{ color: "#555555" }}>{prescription.follow_up_notes}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right Column - No Follow-up Toggle */}
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2 p-3 rounded-lg" style={{ backgroundColor: "#f5f5f5" }}>
                          <Checkbox
                            id={`no-followup-${prescription.id}`}
                            checked={prescription.no_followup || false}
                            onCheckedChange={() => handleToggleNoFollowup(prescription)}
                          />
                          <label
                            htmlFor={`no-followup-${prescription.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            style={{ color: "#333333" }}
                          >
                            No follow-up needed
                          </label>
                        </div>
                        {prescription.follow_up_notes && !prescription.no_followup && (
                          <div>
                            <p className="text-xs font-semibold mb-1" style={{ color: "#555555" }}>Follow-up Notes:</p>
                            <p className="text-sm" style={{ color: "#333333" }}>{prescription.follow_up_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: "#652D90" }}>Delete Lab Result?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this lab result? This will permanently remove:
              <ul className="mt-2 ml-4 list-disc">
                <li>The uploaded file</li>
                <li>All extracted lab values</li>
                <li>AI analysis and insights</li>
              </ul>
              <strong className="block mt-2" style={{ color: "#e90d01" }}>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirmed}
              className="rounded-full bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletePrescriptionDialogOpen} onOpenChange={setDeletePrescriptionDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: "#652D90" }}>Delete Prescription?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this prescription? This will permanently remove:
              <ul className="mt-2 ml-4 list-disc">
                <li>The uploaded prescription file</li>
                <li>Extracted medicine information</li>
                <li>Follow-up appointment details</li>
              </ul>
              <strong className="block mt-2" style={{ color: "#e90d01" }}>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeletePrescriptionConfirmed}
              className="rounded-full bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ color: "#652D90" }}>Reschedule Follow-up Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Date</Label>
              <Input
                type="date"
                value={rescheduleData.date}
                onChange={(e) => setRescheduleData({...rescheduleData, date: e.target.value})}
                className="rounded-xl"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Select Time</Label>
              <Input
                type="time"
                value={rescheduleData.time}
                onChange={(e) => setRescheduleData({...rescheduleData, time: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={rescheduleData.notes}
                onChange={(e) => setRescheduleData({...rescheduleData, notes: e.target.value})}
                placeholder="Add any additional notes for the follow-up..."
                className="rounded-xl min-h-[100px]"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setRescheduleDialogOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRescheduleSubmit}
              className="rounded-full"
              style={{ backgroundColor: "#1fb25a" }}
            >
              Confirm Reschedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: "#652D90" }}>Edit Patient Information</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPatient} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                required
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={editFormData.date_of_birth}
                onChange={(e) => setEditFormData({...editFormData, date_of_birth: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Gender</Label>
              <Input
                value={editFormData.gender}
                onChange={(e) => setEditFormData({...editFormData, gender: e.target.value})}
                placeholder="Male/Female/Other"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Blood Group</Label>
              <Select 
                value={editFormData.blood_group} 
                onValueChange={(value) => setEditFormData({...editFormData, blood_group: value})}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select blood group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A+">A+</SelectItem>
                  <SelectItem value="A-">A-</SelectItem>
                  <SelectItem value="B+">B+</SelectItem>
                  <SelectItem value="B-">B-</SelectItem>
                  <SelectItem value="AB+">AB+</SelectItem>
                  <SelectItem value="AB-">AB-</SelectItem>
                  <SelectItem value="O+">O+</SelectItem>
                  <SelectItem value="O-">O-</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                placeholder="patient@example.com"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Medical History</Label>
              <Textarea
                value={editFormData.medical_history}
                onChange={(e) => setEditFormData({...editFormData, medical_history: e.target.value})}
                className="rounded-xl"
                rows={3}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full rounded-full" 
              style={{ backgroundColor: "#652D90" }}
            >
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: "#652D90" }}>Email Lab Report</AlertDialogTitle>
            <AlertDialogDescription>
              {patient.email ? (
                <>
                  The lab report PDF will be sent to: <strong>{patient.email}</strong>
                  <p className="mt-2">The patient will receive the complete lab analysis including all test results and AI-generated insights.</p>
                </>
              ) : (
                <p style={{ color: "#e90d01" }}>Patient email is not set. Please edit patient information to add an email address first.</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" disabled={emailSending}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleEmailReport}
              disabled={!patient.email || emailSending}
              className="rounded-full"
              style={{ backgroundColor: "#652D90" }}
            >
              {emailSending ? "Sending..." : "Send Email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
